import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardApi } from '../services/api';
import OnboardingStepper from '../components/OnboardingStepper';
import Skeleton from '../components/Skeleton';
import '../App.css';

const NOK = new Intl.NumberFormat('nb-NO', { style: 'currency', currency: 'NOK' });

function toList(value) {
  if (Array.isArray(value)) {
    return value.map((entry) => (typeof entry === 'string' ? entry : JSON.stringify(entry))).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(/\n+/u)
      .map((line) => line.trim())
      .filter(Boolean);
  }
  return [];
}

function extractPlanHighlights(plan, limit = 6) {
  if (!plan) return [];
  const candidates = [
    plan?.metadata?.marketingFeatures,
    plan?.metadata?.marketing_features,
    plan?.metadata?.highlights,
    plan?.features?.marketingHighlights,
    plan?.features?.highlights,
    plan?.features?.summary,
    plan?.features?.notes,
  ];
  for (const candidate of candidates) {
    const list = toList(candidate);
    if (list.length > 0) {
      return limit ? list.slice(0, limit) : list;
    }
  }
  return [];
}

function formatPlanPrice(plan) {
  if (!plan) return '';
  const display = plan?.metadata?.displayPrice || plan?.displayPrice || null;
  if (display) return String(display);

  const planRoleRaw = plan?.features?.planRole || plan?.metadata?.plan_role || plan?.metadata?.planRole || null;
  const planRole = planRoleRaw ? String(planRoleRaw).trim().toLowerCase() : null;
  if (planRole === 'payasyougo') {
    const tokenPriceRaw = plan?.features?.tokenPricePer1k ?? plan?.metadata?.token_price_per_1k ?? plan?.metadata?.tokenPricePer1k ?? NaN;
    const tokenPrice = Number(tokenPriceRaw);
    if (Number.isFinite(tokenPrice) && tokenPrice > 0) {
      return `${NOK.format(tokenPrice)} / 1 000 tokens`;
    }
    return 'Pris per 1 000 tokens';
  }

  const monthlyPrice = Number(plan?.monthlyPrice ?? plan?.monthly_price ?? NaN);
  const stripeMonthly = Number(plan?.stripePrices?.monthly?.unitAmount ?? NaN);
  const amount =
    Number.isFinite(monthlyPrice) && monthlyPrice > 0
      ? monthlyPrice
      : Number.isFinite(stripeMonthly) && stripeMonthly > 0
        ? stripeMonthly
        : (Number.isFinite(monthlyPrice) ? monthlyPrice : NaN);

  if (Number.isFinite(amount)) {
    return amount > 0 ? `${NOK.format(amount)} / mnd` : `${NOK.format(0)} / mnd`;
  }
  return '';
}

export default function PartnerInviteCompanyPage() {
  const { createCompany, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [plans, setPlans] = useState([]);

  const [companyName, setCompanyName] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [organizationNumber, setOrganizationNumber] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [stage, setStage] = useState('loading'); // loading | company | payment | finishing
  const [createdCompanyId, setCreatedCompanyId] = useState(null);
  const [checkoutStarting, setCheckoutStarting] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [slowStripeHint, setSlowStripeHint] = useState(false);
  const checkoutStartedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setCheckoutError(null);
    (async () => {
      try {
        const params = new URLSearchParams(location.search);
        const payment = params.get('payment');
        const companyId = params.get('companyId');
        if (payment === 'cancel') {
          await DashboardApi.partnerInvitePaymentCancel(companyId || null);
          if (typeof logout === 'function') {
            await logout();
          }
          navigate('/login', { replace: true });
          return;
        }
        if (payment === 'success') {
          await DashboardApi.partnerInvitePaymentSuccess(companyId || null);
          navigate('/', { replace: true });
          return;
        }

        const [pendingInvite, planPayload] = await Promise.all([
          DashboardApi.getPendingPartnerInvite(),
          DashboardApi.getBillingPlans().catch(() => ({ plans: [] })),
        ]);
        if (cancelled) return;

        if (!pendingInvite?.pending || !pendingInvite?.invite) {
          setInvite(null);
          navigate('/companies', { replace: true });
          return;
        }

        setInvite(pendingInvite.invite);
        setPlans(Array.isArray(planPayload?.plans) ? planPayload.plans : []);

        const pendingCompanyId =
          pendingInvite.invite.pendingCompanyId
          || pendingInvite.invite.pending_company_id
          || null;

        if (pendingInvite.invite.requirePaymentMethod && pendingCompanyId) {
          setCreatedCompanyId(String(pendingCompanyId));
          setStage('payment');
        } else {
          setStage('company');
        }
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Kunne ikke hente invitasjonen.');
        setStage('company');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.search, navigate, logout]);

  useEffect(() => {
    if (!invite) return;
    if (!companyName.trim() && invite.companyName) setCompanyName(invite.companyName);
    if (!companyEmail.trim() && invite.companyEmail) setCompanyEmail(invite.companyEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invite]);

  const assignedPlan = useMemo(() => {
    if (!invite?.planCode) return null;
    return plans.find((plan) => String(plan.code) === String(invite.planCode)) || null;
  }, [invite?.planCode, plans]);

  const assignedPlanFeatures = useMemo(() => extractPlanHighlights(assignedPlan, 7), [assignedPlan]);

  const steps = useMemo(() => {
    const base = [
      { id: 'account', label: 'Lag konto' },
      { id: 'company', label: 'Opprett selskap' },
    ];
    if (invite?.requirePaymentMethod) {
      return [...base, { id: 'payment', label: 'Fullfør' }];
    }
    return base;
  }, [invite?.requirePaymentMethod]);

  const completedStepIds = useMemo(() => {
    if (stage === 'payment' || stage === 'finishing') return ['account', 'company'];
    return ['account'];
  }, [stage]);

  const activeStepId = useMemo(() => {
    if (stage === 'payment' || stage === 'finishing') return 'payment';
    return 'company';
  }, [stage]);

  const stepTitle = useMemo(() => {
    if (stage === 'payment' || stage === 'finishing') return 'Fullfør';
    return 'Opprett selskap';
  }, [stage]);

  const stepSubtitle = useMemo(() => {
    if (stage === 'payment' || stage === 'finishing') {
      return 'Vent litt mens vi fullfører oppsettet. Du blir sendt videre automatisk.';
    }
    return 'Bekreft selskapsinformasjon og fullfør oppsettet.';
  }, [stage]);

  const startCheckout = useCallback(async (companyId) => {
    const id = companyId ? String(companyId) : '';
    if (!id) return;
    if (checkoutStartedRef.current) return;
    checkoutStartedRef.current = true;

    setCheckoutStarting(true);
    setCheckoutError(null);
    setSlowStripeHint(false);
    try {
      const response = await DashboardApi.partnerInvitePaymentStart(id);
      if (response?.url) {
        window.location.href = response.url;
        return;
      }
      setCheckoutError('Kunne ikke starte Stripe. Prøv igjen.');
    } catch (err) {
      setCheckoutError(err.message || 'Kunne ikke starte Stripe. Prøv igjen.');
    } finally {
      setCheckoutStarting(false);
    }
  }, []);

  useEffect(() => {
    if (stage !== 'payment') return undefined;
    if (!invite?.requirePaymentMethod) return undefined;
    if (!createdCompanyId) return undefined;
    startCheckout(createdCompanyId);
    return undefined;
  }, [createdCompanyId, invite?.requirePaymentMethod, stage, startCheckout]);

  useEffect(() => {
    if (!checkoutStarting) return undefined;
    const timer = setTimeout(() => setSlowStripeHint(true), 12_000);
    return () => clearTimeout(timer);
  }, [checkoutStarting]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);

    if (!invite?.planCode) {
      setError('Mangler plan fra invitasjonen. Kontakt support.');
      return;
    }
    if (!companyName.trim()) {
      setError('Selskapsnavn er påkrevd.');
      return;
    }
    if (!organizationNumber.trim()) {
      setError('Organisasjonsnummer er påkrevd.');
      return;
    }
    if (!companyEmail.trim()) {
      setError('E-postadresse for selskapet er påkrevd.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await createCompany({
        name: companyName.trim(),
        organizationNumber: organizationNumber.replace(/\s+/g, '').trim(),
        companyEmail: companyEmail.trim(),
        planCode: invite.planCode,
        skipCheckout: true,
      });

      const newCompanyId = response?.company?.id || response?.companyId || response?.id || null;
      if (invite?.requirePaymentMethod) {
        if (newCompanyId) {
          setCreatedCompanyId(String(newCompanyId));
        }
        setStage('payment');
        checkoutStartedRef.current = false;
        if (newCompanyId) {
          startCheckout(String(newCompanyId));
        }
        return;
      }

      navigate('/', { replace: true });
    } catch (err) {
      setError(err.message || 'Kunne ikke opprette selskapet.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="auth-layout">
        <div className="auth-card">
          <div className="auth-success">Forbereder oppsett…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-layout">
      <div className="auth-card">
        <header className="auth-header">
          <span className="auth-badge">KI Chat</span>
          <h1>{stepTitle}</h1>
          <p>{stepSubtitle}</p>
        </header>

        <OnboardingStepper
          steps={steps}
          activeStepId={activeStepId}
          completedStepIds={completedStepIds}
        />

        {invite ? (
          <div className="auth-info" style={{ marginBottom: 16 }}>
            Invitasjon fra <strong>{invite.partnerName || 'partner'}</strong>.
          </div>
        ) : null}

        <div className="onboarding-card" style={{ marginBottom: 16 }}>
          <div className="onboarding-card-header">
            <p className="onboarding-card-title">{assignedPlan?.name || invite?.planCode || 'Plan'}</p>
            <span className="onboarding-card-status">{assignedPlan ? formatPlanPrice(assignedPlan) : ''}</span>
          </div>
          {assignedPlanFeatures.length > 0 ? (
            <div className="onboarding-card-body">
              <ul className="company-plan-feature-list" style={{ margin: 0, paddingLeft: 18 }}>
                {assignedPlanFeatures.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>

        {invite?.requirePaymentMethod && stage === 'payment' ? (
          <div className="onboarding-card" style={{ marginBottom: 16 }}>
            <div className="onboarding-card-header">
              <p className="onboarding-card-title">Fullfør</p>
              <span className="onboarding-card-status">{checkoutStarting ? 'Pågår' : checkoutError ? 'Feil' : 'Klar'}</span>
            </div>
            <div className="onboarding-card-body">
              {checkoutError ? (
                <div className="auth-error" style={{ marginBottom: 12 }}>
                  ⚠️ {checkoutError}
                </div>
              ) : null}
              {checkoutStarting ? (
                <div>
                  <div style={{ display: 'grid', gap: 10 }}>
                    <Skeleton variant="line" size="lg" style={{ width: '65%' }} />
                    <Skeleton variant="line" style={{ width: '90%' }} />
                    <Skeleton variant="block" style={{ height: 52, borderRadius: 16 }} />
                  </div>
                  {slowStripeHint ? (
                    <p className="onboarding-card-muted" style={{ marginTop: 10 }}>
                      Dette kan ta litt tid dersom Stripe er treg. Ikke lukk siden.
                    </p>
                  ) : null}
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="button button-primary"
                    onClick={() => {
                      checkoutStartedRef.current = false;
                      startCheckout(createdCompanyId);
                    }}
                    disabled={!createdCompanyId}
                  >
                    Prøv igjen
                  </button>
                  <button
                    type="button"
                    className="button button-secondary"
                    onClick={async () => {
                      try {
                        await DashboardApi.partnerInvitePaymentCancel(createdCompanyId);
                        if (typeof logout === 'function') {
                          await logout();
                        }
                        navigate('/login', { replace: true });
                      } catch (err) {
                        setCheckoutError(err.message || 'Kunne ikke avbryte. Prøv igjen.');
                      }
                    }}
                  >
                    Avbryt
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : null}

        <form className="auth-form" onSubmit={handleSubmit}>
          <label className="auth-label" htmlFor="companyName">
            Selskapsnavn
            <input
              type="text"
              id="companyName"
              className="input"
              value={companyName}
              onChange={(event) => setCompanyName(event.target.value)}
              placeholder="Kunde AS"
              required
              disabled={submitting || stage === 'payment' || stage === 'finishing'}
            />
          </label>

          <label className="auth-label" htmlFor="organizationNumber">
            Organisasjonsnummer
            <input
              type="text"
              id="organizationNumber"
              className="input"
              value={organizationNumber}
              onChange={(event) => setOrganizationNumber(event.target.value)}
              placeholder="999 999 999"
              required
              disabled={submitting || stage === 'payment' || stage === 'finishing'}
            />
          </label>

          <label className="auth-label" htmlFor="companyEmail">
            Selskapets e-post
            <input
              type="email"
              id="companyEmail"
              className="input"
              value={companyEmail}
              onChange={(event) => setCompanyEmail(event.target.value)}
              placeholder="post@kunde.no"
              required
              disabled={submitting || stage === 'payment' || stage === 'finishing'}
            />
          </label>

          {error ? <div className="auth-error">⚠️ {error}</div> : null}

          {stage !== 'payment' && stage !== 'finishing' ? (
            <button type="submit" className="button button-primary" disabled={submitting}>
              {submitting ? 'Oppretter selskap…' : 'Opprett selskap'}
            </button>
          ) : null}
        </form>
      </div>
    </div>
  );
}
